import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useLicenseStore from '../store/licenseStore';
import { KeyRound, AlertTriangle, CheckCircle, Loader2, GraduationCap, ChevronRight, PlayCircle } from 'lucide-react';
import { useTranslation } from '../i18n';
import HSTLogo from '../components/HSTLogo';
import useDemoTracker from '../hooks/useDemoTracker';

export default function LicenseGate() {
  const { t } = useTranslation();
  const { validateLicense } = useLicenseStore();
  const [key, setKey] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Mode toggle: 'user' (default — play the game) or 'master' (supervision).
  const [mode, setMode] = useState('user');
  const { track } = useDemoTracker();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (key.trim().length < 8) {
      setError(t('license_too_short'));
      return;
    }
    setLoading(true);
    setError('');
    if (mode === 'master') {
      // Master key validation goes directly to /api/master/validate.
      // We send `credentials: 'include'` so the httpOnly session cookie
      // (hst_master_session) sent back by the server is stored by the
      // browser and replayed on subsequent /api/master/* calls. The
      // master license key itself is NEVER persisted in JS-accessible
      // storage anymore — only the holder display name is kept (for
      // the header on the supervision dashboard).
      try {
        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/master/validate`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ license_key: key.trim().toUpperCase() }),
        });
        if (!resp.ok) {
          // Body may have been already consumed by a platform service worker
          // or browser extension; rely on status code for a contextual message
          // and try resp.json() once as best-effort enrichment.
          let detail = '';
          try {
            const j = await resp.json();
            detail = (j && j.detail) || '';
          } catch (_) { /* body already consumed — fall through */ }
          if (!detail) {
            if (resp.status === 400) {
              detail = t('master_login_not_master_key');
            } else if (resp.status === 403) {
              detail = t('master_login_disabled_or_expired');
            } else if (resp.status === 404) {
              detail = t('master_login_invalid_key');
            }
          }
          setError(detail || t('invalid_key'));
          setLoading(false);
          return;
        }
        const data = await resp.json();
        // ⚠️ Security : the license_key is intentionally NOT echoed back
        // in the response body anymore — auth lives in the cookie. We only
        // persist the holder display name so the supervision header can
        // greet the supervisor without a network round-trip.
        try {
          sessionStorage.setItem('hst-master-holder', data.holder_name || '');
        } catch (_) {}
        // Navigate to /supervision.
        window.location.href = '/supervision';
      } catch (err) {
        setError(err.message || t('invalid_key'));
      }
      setLoading(false);
      return;
    }
    const result = await validateLicense(key);
    setLoading(false);
    if (!result.valid) {
      setError(result.error || t('invalid_key'));
    }
  };

  const isMaster = mode === 'master';

  return (
    <div className="min-h-screen bg-ink-900 flex items-center justify-center p-4 relative overflow-hidden" data-testid="license-gate">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-25 dark:opacity-25"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1629881635342-c1272d45d0fa?auto=format&fit=crop&w=1600&q=80)' }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-background/90 via-background/80 to-background" />
      <div className="absolute inset-0 bg-grid-dots opacity-30" />

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex flex-col items-center gap-3">
            <HSTLogo size={72} />
            <div>
              <h1 className="font-display text-5xl font-black tracking-tight text-foreground">HST</h1>
              <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400 font-mono mt-2">{t('hst_subtitle')}</p>
            </div>
          </div>
        </div>

        <div className="surface-card bg-card/95 backdrop-blur-xl border-ink-600 overflow-hidden">
          <div className={`bg-gradient-to-r ${isMaster ? 'from-purple-700 to-purple-900' : 'from-ink-700 to-ink-800'} px-6 py-4 border-b border-ink-600 transition-colors`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-md ${isMaster ? 'bg-white/15 border border-white/30' : 'bg-accent/15 border border-accent/30'}`}>
                {isMaster
                  ? <GraduationCap className="w-5 h-5 text-white" />
                  : <KeyRound className="w-5 h-5 text-accent" />}
              </div>
              <div>
                <h2 className={`font-display font-bold tracking-tight ${isMaster ? 'text-white' : 'text-foreground'}`}>
                  {isMaster ? t('master_space_title') : t('license_activation')}
                </h2>
                <p className={`text-[10px] uppercase tracking-wider font-mono mt-0.5 ${isMaster ? 'text-white/80' : 'text-slate-400'}`}>
                  {isMaster ? t('master_space_subtitle') : t('enter_key_hint')}
                </p>
              </div>
            </div>
          </div>

          {/* Mode toggle */}
          <div className="grid grid-cols-2 border-b border-ink-600">
            <button
              type="button"
              onClick={() => { setMode('user'); setError(''); }}
              className={`py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                mode === 'user' ? 'bg-accent/10 text-accent border-b-2 border-accent' : 'text-slate-500 hover:text-slate-300'
              }`}
              data-testid="mode-user"
            >
              {t('mode_apprentice')}
            </button>
            <button
              type="button"
              onClick={() => { setMode('master'); setError(''); }}
              className={`py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                mode === 'master' ? 'bg-purple-500/10 text-purple-300 border-b-2 border-purple-400' : 'text-slate-500 hover:text-slate-300'
              }`}
              data-testid="mode-master"
            >
              {t('mode_master')}
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="overline block mb-2">
                {isMaster ? t('master_key_label') : t('license_key')}
              </label>
              <input
                type="text"
                value={key}
                onChange={(e) => setKey(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX-XXXX-XXXX"
                className="w-full bg-background border border-ink-600 rounded-md px-4 py-3 text-center text-lg font-mono tracking-widest text-foreground placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent uppercase"
                maxLength={30}
                data-testid="license-key-input"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-danger/10 border border-danger/40 rounded-md px-4 py-3" data-testid="license-error">
                <AlertTriangle className="w-4 h-4 text-danger flex-shrink-0" />
                <p className="text-sm text-danger">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || key.trim().length < 8}
              className={`w-full ${isMaster ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'btn-tech-primary'} py-3 rounded disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 uppercase tracking-wider transition-colors`}
              data-testid="license-submit"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> {t('verifying')}</>
              ) : isMaster ? (
                <>{t('access_supervision')} <ChevronRight className="w-4 h-4" /></>
              ) : (
                <><CheckCircle className="w-4 h-4" /> {t('activate_license')}</>
              )}
            </button>
          </form>

          <div className="px-6 pb-5">
            <p className="text-[10px] text-slate-500 text-center font-mono uppercase tracking-wider">
              {isMaster
                ? t('master_supervision_only_note')
                : t('contact_admin_no_key')}
            </p>
          </div>
        </div>

        {/* Public demo CTA — visible for both user & master flows */}
        <Link
          to="/demo"
          onClick={() => track('banner_clicked', 'gate')}
          className="mt-4 mx-auto flex items-center justify-center gap-2 w-full max-w-sm rounded-full border border-accent/50 bg-accent/10 hover:bg-accent/20 text-accent px-4 py-3 text-sm font-bold uppercase tracking-wider transition-colors group"
          data-testid="demo-cta-banner"
        >
          <PlayCircle className="w-4 h-4 group-hover:scale-110 transition-transform" />
          {t('demo_cta_banner')}
          <ChevronRight className="w-3 h-3 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
        </Link>
      </div>
    </div>
  );
}
